import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Workflow,
  Loader2,
  AlertTriangle,
  ArrowUpRight,
  Plus,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { listTenants } from "../services/tenant";
import { listSagas } from "../services/saga";
import { StatusPill } from "../components/saga/StatusPill";
import { Button } from "../components/ui/button";
import { RequireRole } from "../components/RequireRole";
import { useAuthUser } from "../hooks/useAuthUser";
import { cn } from "../utils/cn";
import type { SagaRunResult } from "../types/saga";
import type { Tenant } from "../types/tenant";

interface Stats {
  totalTenants: number;
  activeTenants: number;
  totalSagas: number;
  runningSagas: number;
  failedNeedsManual: number;
}

export default function Home() {
  const { user } = useAuthUser();
  const [stats, setStats] = useState<Stats | null>(null);
  const [recentSagas, setRecentSagas] = useState<SagaRunResult[] | null>(null);
  const [recentTenants, setRecentTenants] = useState<Tenant[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [tenantsRes, sagasRes, runningRes, manualRes] = await Promise.all([
          listTenants({ limit: 5 }),
          listSagas({ limit: 5 }),
          listSagas({ limit: 1, status: "RUNNING" }),
          listSagas({ limit: 1, status: "FAILED_NEEDS_MANUAL" }),
        ]);
        if (!alive) return;
        const activeTenants = tenantsRes.data.filter((t) => t.status === "ACTIVE").length;
        setStats({
          totalTenants: tenantsRes.pagination.total,
          activeTenants,
          totalSagas: sagasRes.pagination.total,
          runningSagas: runningRes.pagination.total,
          failedNeedsManual: manualRes.pagination.total,
        });
        setRecentSagas(sagasRes.data);
        setRecentTenants(tenantsRes.data);
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : "Erro carregando dashboard");
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={user ? `Bem-vindo, ${user.name?.split(" ")[0] ?? user.sub}.` : undefined}
        actions={
          <RequireRole anyOf={["platform-master", "platform-admin", "platform-operations", "dev-on-call"]}>
            <Link to="/onboardings/new">
              <Button size="sm">
                <Plus size={14} />
                Novo tenant
              </Button>
            </Link>
          </RequireRole>
        }
      />

      <div className="p-6 space-y-6">
        {err && (
          <div className="p-3 rounded-md bg-danger-soft text-danger-soft-fg text-sm">
            {err}
          </div>
        )}

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            label="Tenants ativos"
            value={stats ? `${stats.activeTenants}` : "—"}
            sub={stats ? `${stats.totalTenants} total` : undefined}
            icon={Building2}
            tone="default"
            loading={!stats}
          />
          <StatCard
            label="Sagas (total)"
            value={stats ? `${stats.totalSagas}` : "—"}
            icon={Workflow}
            tone="default"
            loading={!stats}
          />
          <StatCard
            label="Em execução"
            value={stats ? `${stats.runningSagas}` : "—"}
            icon={Loader2}
            tone={stats && stats.runningSagas > 0 ? "primary" : "default"}
            loading={!stats}
          />
          <StatCard
            label="Atenção manual"
            value={stats ? `${stats.failedNeedsManual}` : "—"}
            icon={AlertTriangle}
            tone={stats && stats.failedNeedsManual > 0 ? "warning" : "default"}
            loading={!stats}
          />
        </div>

        {/* Two columns: recent sagas + recent tenants */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Section
            title="Últimas sagas"
            href="/sagas"
            empty={recentSagas?.length === 0}
            loading={recentSagas === null}
          >
            <ul className="divide-y divide-border-subtle">
              {recentSagas?.map((s) => (
                <li key={s.sagaId}>
                  <Link
                    to={`/sagas/${s.sagaId}`}
                    className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-surface-2 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <code className="font-mono-tight text-xs text-muted-foreground shrink-0">
                        {s.sagaId.slice(0, 8)}
                      </code>
                      <span className="text-sm font-medium truncate">{s.sagaType}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <StatusPill status={s.status} size="xs" />
                      <span className="text-xs text-muted-foreground font-mono-tight">
                        {s.currentStep}/{s.totalSteps}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </Section>

          <Section
            title="Tenants recentes"
            href="/tenants"
            empty={recentTenants?.length === 0}
            loading={recentTenants === null}
          >
            <ul className="divide-y divide-border-subtle">
              {recentTenants?.map((t) => (
                <li key={t.tenantKey} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="flex items-center gap-3 min-w-0">
                    {t.brandingColor && (
                      <span
                        className="w-3 h-3 rounded shrink-0 border border-border"
                        style={{ backgroundColor: t.brandingColor }}
                        aria-hidden="true"
                      />
                    )}
                    <code className="font-mono-tight text-xs text-muted-foreground shrink-0">
                      {t.tenantKey}
                    </code>
                    <span className="text-sm font-medium truncate">{t.name}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-[10px] uppercase font-mono-tight text-muted-foreground tracking-wider">
                      {t.billingTier}
                    </span>
                    <StatusPill status={t.status as never} size="xs" />
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
  icon: typeof Building2;
  tone: "default" | "primary" | "warning";
  loading?: boolean;
}

function StatCard({ label, value, sub, icon: Icon, tone, loading }: StatCardProps) {
  const TONE_CLS: Record<string, string> = {
    default: "border-border bg-surface",
    primary: "border-primary/30 bg-primary-soft/30",
    warning: "border-warning/40 bg-warning-soft/40",
  };
  const ICON_CLS: Record<string, string> = {
    default: "text-muted-foreground",
    primary: "text-primary",
    warning: "text-warning-soft-fg",
  };
  return (
    <div className={cn("rounded-md border p-4 flex items-start gap-3", TONE_CLS[tone])}>
      <div className={cn("p-2 rounded bg-surface-2", ICON_CLS[tone])}>
        <Icon size={16} strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          {label}
        </p>
        <p className={cn("text-xl font-semibold mt-0.5", loading && "opacity-50")}>
          {value}
        </p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

interface SectionProps {
  title: string;
  href: string;
  loading?: boolean;
  empty?: boolean;
  children: React.ReactNode;
}

function Section({ title, href, loading, empty, children }: SectionProps) {
  return (
    <div className="rounded-md border border-border bg-surface overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border-subtle bg-surface-1">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        <Link
          to={href}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          Ver tudo <ArrowUpRight size={12} />
        </Link>
      </div>
      {loading ? (
        <div className="p-6 text-sm text-muted-foreground italic">Carregando…</div>
      ) : empty ? (
        <div className="p-6 text-sm text-muted-foreground italic">Nada por aqui ainda.</div>
      ) : (
        children
      )}
    </div>
  );
}
